import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, HStack, useBreakpointValue, useColorMode } from '@chakra-ui/react';
import { Calendar as ReactCalendar, Views, ToolbarProps } from 'react-big-calendar';
import { isSameDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { Event, CalendarToolbar } from './index';
import { Agenda, Month, Week } from './views';
import {
	anyFilterEnabled,
	LAYOUT_ALGORITHM,
	createLocalizer,
	RADIUS,
	useCalendarSettings,
	useContainerWidth,
	useFilters,
	DEFAULT_WEEK_DAY,
	useShortcodeSettings,
} from '../../lib';
import { Loader } from '../ui';
import { RenderFilters, RenderHorizontalFilters } from './toolbar/filters';
import { EventWrapper, MonthDateHeader } from './wrappers';

interface ICalendarProps {}

export const Calendar: React.FunctionComponent<ICalendarProps> = () => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const { filteredEvents: events, currentView, setCurrentView, setCurrentMonth } = useFilters();
	const { settings: shortcodeSettings } = useShortcodeSettings();
	const isMobile = useBreakpointValue({ base: true, md: false });
	const [toolbarProps, setToolbarProps] = useState<ToolbarProps>();
	const { containerRef, isNarrow } = useContainerWidth();

	const updateToolbarProps = useCallback(
		(props) => {
			if (props?.label !== toolbarProps?.label || !isSameDay(props?.date, toolbarProps?.date)) {
				setToolbarProps(props);
			}
		},
		[toolbarProps]
	);
	const views = useMemo(() => {
		const views = [];
		if (settings.monthView) views.push(Views.MONTH);
		if (settings.weekView) views.push(Views.WEEK);
		if (settings.dayView) views.push(Views.DAY);
		if (settings.agendaView) views.push(Views.AGENDA);
		return views;
	}, [settings]);

	useEffect(() => {
		if (settings.defaultView) setCurrentView(settings.defaultView);
	}, [settings.defaultView, setCurrentView]);

	useLayoutEffect(() => {
		const borderRadius = RADIUS[settings.radius.calendarView];
		document.documentElement.style.setProperty('--calendar-border-radius', borderRadius);
	}, [settings.radius.calendarView]);

	const enabledViews = views.reduce((acc, view) => {
		let viewComponent: React.ComponentType<any> | boolean = true;
		if (view === Views.MONTH) {
			viewComponent = isMobile ? Month : true;
		} else if (view === Views.WEEK) {
			viewComponent = Week;
		} else if (view === Views.AGENDA) {
			viewComponent = Agenda;
		}
		return { ...acc, [view]: viewComponent };
	}, {});

	const calendarStyles = useMemo(
		() => ({
			minHeight: 800,
			backgroundColor: settings.styles[colorMode].calendarBackgroundColor || '#fff',
			color: settings.styles[colorMode].calendarTextColor || '#2D3539',
			borderRadius: RADIUS[settings.radius.calendarView],
		}),
		[settings.styles, colorMode, settings.radius.calendarView]
	);

	const calendarComponents = useMemo(
		() => ({
			event: Event,
			toolbar: (props: ToolbarProps) => <EmptyToolbar {...props} updateToolbarProps={updateToolbarProps} />,
			eventWrapper: EventWrapper,
			month: {
				dateHeader: MonthDateHeader,
			},
		}),
		[updateToolbarProps]
	);
	const localizer = useMemo(
		() => createLocalizer(settings?.startOfWeek ?? DEFAULT_WEEK_DAY),
		[settings?.startOfWeek]
	);

	const mobileClass = isMobile ? 'mobile' : 'desktop';
	const { filtersBackgroundColor, calendarPlaceholderCellBackgroundColor } = settings.styles[colorMode];
	const { enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter } =
		settings;

	// Checks for rendering filters through settings/shortcodes
	const filtersEnabledInSettings = settings?.enableCalendarFilters;
	const isDesktop = !isMobile;
	const shortcodeFiltersAllowed = shortcodeSettings.filters !== false;
	const filtersExplicitlyEnabled = shortcodeSettings.filters === true;
	const wideScreen = !isNarrow;
	const topFiltersAreHidden = !settings.showFiltersOnTop;
	const anyFiltersEnabled = useMemo(
		() =>
			anyFilterEnabled(
				enableSearchFilter,
				enableLocationFilter,
				enableCategoryFilter,
				enableVenueFilter,
				enableTagFilter
			),
		[enableSearchFilter, enableLocationFilter, enableCategoryFilter, enableVenueFilter, enableTagFilter]
	);

	if (!events || !currentView) return <Loader />;

	const showSidebarFilters =
		filtersEnabledInSettings &&
		isDesktop &&
		shortcodeFiltersAllowed &&
		wideScreen &&
		topFiltersAreHidden &&
		(anyFiltersEnabled || filtersExplicitlyEnabled);

	const showtopBarFilters =
		filtersEnabledInSettings &&
		shortcodeFiltersAllowed &&
		settings.showFiltersOnTop &&
		(anyFiltersEnabled || filtersExplicitlyEnabled);
	return (
		<Box
			ref={containerRef}
			width={settings?.enableCalendarFilters ? 'full' : 'clamp(30rem, 60rem, 100%)'}
			marginLeft='auto'
			marginRight='auto'
		>
			{toolbarProps && <CalendarToolbar setCurrentMonth={setCurrentMonth} {...toolbarProps} />}
			{showtopBarFilters && (
				<Box px={1} background={filtersBackgroundColor}>
					<RenderHorizontalFilters />
				</Box>
			)}
			<HStack alignItems='flex-start' overflowX='scroll' minH='80vh' background={filtersBackgroundColor} px={0}>
				{showSidebarFilters && (
					<Box px={1}>
						<RenderFilters isVisibleOnView={true} />
					</Box>
				)}
				<style>{`
					.rbc-off-range-bg {
						background-color: ${calendarPlaceholderCellBackgroundColor} !important;
					}
				`}</style>
				<ReactCalendar
					className={`cal-plus-${currentView}-view ${mobileClass}`}
					components={calendarComponents}
					dayLayoutAlgorithm={LAYOUT_ALGORITHM.NO_OVERLAP}
					defaultView={currentView}
					events={events}
					localizer={localizer}
					onView={setCurrentView}
					popup={true}
					showAllEvents={true}
					style={calendarStyles}
					view={currentView}
					views={enabledViews}
				/>
			</HStack>
		</Box>
	);
};

const EmptyToolbar: React.FC<ToolbarProps & { setToolbarParams: (params: ToolbarProps) => void }> = (props) => {
	const { updateToolbarProps, ...toolbarProps } = props;
	useEffect(() => {
		updateToolbarProps(toolbarProps);
	}, [props, updateToolbarProps, toolbarProps]);
	return <></>;
};
